/**
 * 曝光度对话框（参照 Photoshop「图像→调整→曝光度」，破坏性）。
 * 单一 EV 滑杆，实时预览，确定后一次性改活动图层像素。
 */
import type { JSX } from 'solid-js';
import { Show, createEffect, createSignal } from 'solid-js';
import { useFocusTrap } from '../a11y/focus-trap';
import styles from './ExposureDialog.module.css';

export interface ExposureDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onApply: (exposure: number) => void;
  readonly previewUrl?: string | undefined;
  readonly onPreview?: ((exposure: number) => void) | undefined;
}

export function ExposureDialog(props: ExposureDialogProps): JSX.Element {
  const [exposure, setExposure] = createSignal(0);
  let dialogRef: HTMLDivElement | undefined;
  useFocusTrap(() => props.open, () => dialogRef ?? null, props.onClose);

  createEffect(() => {
    if (props.open) setExposure(0);
  });

  createEffect(() => {
    if (!props.open) return;
    props.onPreview?.(exposure());
  });

  return (
    <Show when={props.open}>
      <div class={styles.backdrop} onClick={props.onClose} data-testid="exposure-backdrop">
        <div
          class={styles.dialog}
          role="dialog"
          ref={(el) => (dialogRef = el)}
          aria-label="曝光度"
          data-testid="exposure-dialog"
          onClick={(e) => e.stopPropagation()}
        >
          <h2 class={styles.title}>曝光度</h2>

          <Show when={props.previewUrl !== undefined}>
            <div class={styles.previewPanel}>
              <img class={styles.previewImage} src={props.previewUrl} alt="曝光度预览" data-testid="exposure-preview" />
            </div>
          </Show>

          <label class={styles.row}>
            <span class={styles.label}>曝光度</span>
            <input
              type="range"
              min="-5"
              max="5"
              step="0.1"
              value={exposure()}
              data-testid="exposure-value"
              onInput={(e) => setExposure(Number(e.currentTarget.value))}
            />
            <span class={styles.value}>{exposure().toFixed(1)}</span>
          </label>

          <div class={styles.actions}>
            <button type="button" class={styles.secondary} onClick={props.onClose}>
              取消
            </button>
            <button
              type="button"
              class={styles.primary}
              data-testid="exposure-confirm"
              onClick={() => props.onApply(exposure())}
            >
              确定
            </button>
          </div>
        </div>
      </div>
    </Show>
  );
}
